import { useEffect, useRef, useState } from 'react';
import { registerReauthentication, redirectToLogin } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { getSession, setSession } from '@/auth/session';
import { Button } from '@/design/components/Button';
import { Dialog } from '@/design/components/Dialog';
import { Banner } from '@/design/components/Display';
import { Field, TextInput } from '@/design/components/Field';
import { errorMessage } from '@/i18n/errors';

/**
 * When the 8-hour session ends in the middle of work, this asks for the password in place: whatever the person was
 * typing stays on screen and the failed request is retried after logging in again.
 */
export function ReauthDialog() {
  const { login } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    registerReauthentication(
      () =>
        new Promise<boolean>((resolve) => {
          setEmail(getSession()?.user.email ?? '');
          setPassword('');
          setError(null);
          resolver.current = resolve;
          setOpen(true);
        }),
    );
    return () => registerReauthentication(null);
  }, []);

  const finish = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
    if (!ok) {
      setSession(null);
      redirectToLogin();
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      finish(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && finish(false)}
      title="Tu sesión terminó"
      description="Por seguridad las sesiones duran 8 horas. Ingresá de nuevo y seguí donde estabas: no se pierde nada."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => finish(false)}>
            Salir
          </Button>
          <Button variant="gold" loading={busy} disabled={!password} onClick={() => void submit()}>
            Continuar
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {error && <Banner tone="danger">{error}</Banner>}
        <Field label="Email">{(p) => <TextInput {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
        <Field label="Contraseña">
          {(p) => <TextInput {...p} type="password" autoFocus autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
